/**
 * Backup Key — Ansatz C
 *
 * Random 256-bit backup key, encrypted with PBKDF2(login password).
 * No extra password, no recovery key to remember.
 *
 * Flow:
 * 1. First login: generate random backupKey → encrypt with password → upload
 * 2. Subsequent logins: download encrypted backupKey → decrypt with password → use
 * 3. After each message decrypt: encrypt plaintext with backupKey → upload backup
 * 4. New device: login → get backupKey → download all backups → decrypt → fill cache
 */

import { api } from "./api";
import {
  recordBackupWrite,
  recordBackupRead,
  recordBackupDecryptOutcome,
} from "./backup-telemetry";
import { isBackupablePlaintext } from "./failsafe-sentinel";

// ── F19 plaintext-mirror toggle ──────────────────────────────────────────────
// Default-on so the server-side `debug_backup_plaintext` table stays consistent
// across all 4 mechanisms (otherwise only bot writes land there). Opt-out per
// browser by setting `localStorage["kp_debug_backup"] = "0"`. Server still
// gates on DEBUG_BACKUP_PLAINTEXT_ENABLED + allow-list — set the server flag
// off for a hard global kill.

const DEBUG_BACKUP_LS_KEY = "kp_debug_backup";

function isDebugBackupOptedIn(): boolean {
  try {
    if (typeof localStorage === "undefined") return true;
    return localStorage.getItem(DEBUG_BACKUP_LS_KEY) !== "0";
  } catch {
    return true;
  }
}

// ── Constants ────────────────────────────────────────────────────────────────

const PBKDF2_ITERATIONS = 600_000;
const BACKUP_KEY_BYTES = 32; // 256-bit

// ── Types ────────────────────────────────────────────────────────────────────

export interface BackupKeyEnvelope {
  encryptedBackupKey: string; // base64
  salt: string;               // base64
  iv: string;                  // base64
}

export interface MessageBackupEntry {
  messageId: string;
  ciphertext: string; // base64 — AES-GCM(backupKey, plaintext)
  nonce: string;      // base64
  createdAt: string;
  senderId?: string;
  contentType?: string;
}

// ── Key Derivation ───────────────────────────────────────────────────────────

/** Derive an AES-256 wrapping key from password + salt via PBKDF2 */
async function deriveKeyFromPassword(
  password: string,
  salt: Uint8Array,
): Promise<CryptoKey> {
  const encoder = new TextEncoder();
  const passwordBytes = encoder.encode(password);
  const keyMaterial = await crypto.subtle.importKey(
    "raw",
    passwordBytes.buffer as ArrayBuffer,
    "PBKDF2",
    false,
    ["deriveKey"],
  );

  return crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: salt.buffer as ArrayBuffer,
      iterations: PBKDF2_ITERATIONS,
      hash: "SHA-256",
    },
    keyMaterial,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

// ── Backup Key Management ────────────────────────────────────────────────────

/** Generate a fresh random backup key */
function generateBackupKey(): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(BACKUP_KEY_BYTES));
}

/** Encrypt the backup key with a password-derived key */
async function encryptBackupKey(
  backupKeyRaw: Uint8Array,
  password: string,
): Promise<BackupKeyEnvelope> {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const wrappingKey = await deriveKeyFromPassword(password, salt);

  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: iv.buffer as ArrayBuffer },
    wrappingKey,
    backupKeyRaw.buffer as ArrayBuffer,
  );

  return {
    encryptedBackupKey: bytesToBase64(new Uint8Array(ciphertext)),
    salt: bytesToBase64(salt),
    iv: bytesToBase64(iv),
  };
}

/** Decrypt the backup key using the login password */
async function decryptBackupKey(
  envelope: BackupKeyEnvelope,
  password: string,
): Promise<Uint8Array> {
  const salt = base64ToBytes(envelope.salt);
  const iv = base64ToBytes(envelope.iv);
  const encryptedData = base64ToBytes(envelope.encryptedBackupKey);

  const wrappingKey = await deriveKeyFromPassword(password, salt);

  const decrypted = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: iv.buffer as ArrayBuffer },
    wrappingKey,
    encryptedData.buffer as ArrayBuffer,
  );

  return new Uint8Array(decrypted);
}

/** Import raw backup key bytes as a CryptoKey for AES-GCM.
 *  extractable=true so reEncryptBackupKey can export raw bytes on password change. */
async function importBackupKey(raw: Uint8Array): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    "raw",
    raw.buffer as ArrayBuffer,
    { name: "AES-GCM", length: 256 },
    true,
    ["encrypt", "decrypt"],
  );
}

// ── Public API ───────────────────────────────────────────────────────────────

const BACKUP_KEY_STORAGE = "kp_backup_key";

/**
 * Initialize backup key: either retrieve existing from server or create new.
 * Call after successful login while password is still in memory.
 * Also caches the raw key in localStorage for session restore.
 * Returns the CryptoKey for encrypting/decrypting backups.
 */
export async function initBackupKey(password: string): Promise<CryptoKey | null> {
  try {
    // Check if server has an encrypted backup key
    const existing = await api.get<{
      exists: boolean;
      encryptedBackupKey?: string;
      salt?: string;
      iv?: string;
    }>("/users/me/backup-key");

    if (existing?.exists && existing.encryptedBackupKey && existing.salt && existing.iv) {
      // Decrypt existing backup key with password
      const rawKey = await decryptBackupKey(
        {
          encryptedBackupKey: existing.encryptedBackupKey,
          salt: existing.salt,
          iv: existing.iv,
        },
        password,
      );
      // Cache raw key for session restore
      localStorage.setItem(BACKUP_KEY_STORAGE, bytesToBase64(rawKey));
      return importBackupKey(rawKey);
    }

    // No backup key yet — generate and upload
    const rawKey = generateBackupKey();
    const envelope = await encryptBackupKey(rawKey, password);

    await api.post("/users/me/backup-key", envelope);

    // Cache raw key for session restore
    localStorage.setItem(BACKUP_KEY_STORAGE, bytesToBase64(rawKey));
    return importBackupKey(rawKey);
  } catch (err) {
    console.error("[backup-key] Init failed:", err);
    return null;
  }
}

/**
 * Restore backup key from localStorage (for session restore without password).
 * Returns null if no cached key exists.
 */
export async function restoreBackupKeyFromCache(): Promise<CryptoKey | null> {
  try {
    const cached = localStorage.getItem(BACKUP_KEY_STORAGE);
    if (!cached) return null;
    const rawKey = base64ToBytes(cached);
    return importBackupKey(rawKey);
  } catch {
    return null;
  }
}

/**
 * Bug D: return the raw base64-encoded backup key from the localStorage cache,
 * or null if not present. Used by the proxy_backup_key_request handler to
 * forward the key to a peer device via X3DH envelope.
 */
export function getRawBackupKeyBase64(): string | null {
  try {
    return localStorage.getItem(BACKUP_KEY_STORAGE);
  } catch {
    return null;
  }
}

/**
 * Bug D: save a raw base64-encoded backup key received from a peer device.
 * Stores in localStorage so restoreBackupKeyFromCache() finds it on next call.
 * Returns the imported CryptoKey so callers can use it immediately.
 */
export async function saveRawBackupKeyBase64(b64: string): Promise<CryptoKey> {
  const rawKey = base64ToBytes(b64);
  localStorage.setItem(BACKUP_KEY_STORAGE, b64);
  return importBackupKey(rawKey);
}

/**
 * Clear cached backup key (call on logout).
 */
export function clearBackupKeyCache(): void {
  localStorage.removeItem(BACKUP_KEY_STORAGE);
}

/**
 * Re-encrypt the backup key after password change.
 * Requires the backup key still in memory + the new password.
 */
export async function reEncryptBackupKey(
  backupKey: CryptoKey,
  newPassword: string,
): Promise<boolean> {
  try {
    // Export raw key bytes
    const rawBytes = await crypto.subtle.exportKey("raw", backupKey);
    const raw = new Uint8Array(rawBytes);

    // Re-encrypt with new password
    const envelope = await encryptBackupKey(raw, newPassword);
    await api.post("/users/me/backup-key", envelope);

    return true;
  } catch (err) {
    console.error("[backup-key] Re-encrypt failed:", err);
    return false;
  }
}

/**
 * Encrypt a plaintext message for backup storage.
 */
export async function encryptForBackup(
  backupKey: CryptoKey,
  plaintext: string,
): Promise<{ ciphertext: string; nonce: string }> {
  const nonce = crypto.getRandomValues(new Uint8Array(12));
  const data = new TextEncoder().encode(plaintext);

  const ciphertextBuf = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: nonce.buffer as ArrayBuffer },
    backupKey,
    data.buffer as ArrayBuffer,
  );

  return {
    ciphertext: bytesToBase64(new Uint8Array(ciphertextBuf)),
    nonce: bytesToBase64(nonce),
  };
}

/**
 * Decrypt a backup entry.
 */
export async function decryptBackup(
  backupKey: CryptoKey,
  ciphertext: string,
  nonce: string,
): Promise<string> {
  const ct = base64ToBytes(ciphertext);
  const iv = base64ToBytes(nonce);

  const plaintextBuf = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: iv.buffer as ArrayBuffer },
    backupKey,
    ct.buffer as ArrayBuffer,
  );

  return new TextDecoder().decode(plaintextBuf);
}

/**
 * Upload a message backup to the server (fire-and-forget, rate-limited).
 * Queues requests and processes max 5 concurrently to avoid flooding.
 */
const backupQueue: Array<{
  backupKey: CryptoKey;
  messageId: string;
  plaintext: string;
  // F24: resolve callback so callers can observe success/failure without
  // turning this into a blocking call — nothing awaits it here, callers opt
  // in by awaiting the returned promise (see cacheAndBackup in useChat.ts).
  resolve: (success: boolean) => void;
}> = [];
let backupInflight = 0;
const MAX_BACKUP_CONCURRENT = 5;

function processBackupQueue(): void {
  while (backupInflight < MAX_BACKUP_CONCURRENT && backupQueue.length > 0) {
    const item = backupQueue.shift()!;
    backupInflight++;
    encryptForBackup(item.backupKey, item.plaintext)
      .then((encrypted) => {
        // F19: opt-in plaintext parallel-write. Only attached when the local
        // debug flag is set. Server gates the actual write behind its own flag
        // + allow-list, so this is a no-op in production unless both opt in.
        const body: { ciphertext: string; nonce: string; _debugPlaintext?: string } = {
          ciphertext: encrypted.ciphertext,
          nonce: encrypted.nonce,
        };
        if (isDebugBackupOptedIn() && isBackupablePlaintext(item.plaintext)) body._debugPlaintext = item.plaintext;
        return api.post(`/messages/${item.messageId}/backup`, body).then(() => {
          recordBackupWrite("per_user");
          item.resolve(true);
        });
      })
      // F24: was a silent fail (e.g. HTTP 429) with no way for the caller to
      // know and retry. Still swallowed here (fire-and-forget stays intact)
      // but now surfaced as `resolve(false)` instead of dropped entirely.
      .catch(() => { item.resolve(false); })
      .finally(() => {
        backupInflight--;
        processBackupQueue();
      });
  }
}

/**
 * F24: returns a promise resolving to true/false on upload success/failure.
 * Never rejects — existing fire-and-forget callers can keep ignoring the
 * return value; callers that care (cacheAndBackup) can await/.then() it to
 * retry on failure.
 */
export function uploadBackup(
  backupKey: CryptoKey,
  messageId: string,
  plaintext: string,
): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    backupQueue.push({ backupKey, messageId, plaintext, resolve });
    processBackupQueue();
  });
}

/**
 * Restore all backups for a channel. Returns decrypted messages.
 */
export async function restoreBackups(
  backupKey: CryptoKey,
  channelId: string,
): Promise<Array<{ messageId: string; plaintext: string; senderId?: string; contentType?: string }>> {
  const results: Array<{ messageId: string; plaintext: string; senderId?: string; contentType?: string }> = [];

  let before: string | undefined;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const url = before
      ? `/users/me/backups/by-channel/${channelId}?limit=100&before=${before}`
      : `/users/me/backups/by-channel/${channelId}?limit=100`;

    const response = await api.get<{ backups: MessageBackupEntry[] }>(url);
    const backups = response?.backups ?? [];
    if (backups.length === 0) break;

    recordBackupRead("per_user", backups.length);

    for (const backup of backups) {
      try {
        const plaintext = await decryptBackup(backupKey, backup.ciphertext, backup.nonce);
        recordBackupDecryptOutcome("per_user", true);
        results.push({
          messageId: backup.messageId,
          plaintext,
          senderId: backup.senderId,
          contentType: backup.contentType,
        });
      } catch (err) {
        // Skip corrupt backups. Reason carries WebCrypto error name for diagnosis.
        const errName = (err as { name?: string } | null)?.name ?? "unknown";
        recordBackupDecryptOutcome("per_user", false, `bulk-restore:${errName}`);
      }
    }

    before = backups[backups.length - 1]!.messageId;
    if (backups.length < 100) break; // Last page
  }

  return results;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]!);
  return btoa(binary);
}

function base64ToBytes(b64: string): Uint8Array {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}
